/**
 * Lithofinder Camera - Hybrid recognition (Browser + Worker)
 *
 * Stage 1 (material): ONNX Runtime Web -> MobileNetV3 in browser (0 API cost)
 * Stage 2 (subtype):  Worker API -> Gemini (only for Lithofin-relevant materials)
 * Problem mode:       Worker API -> Gemini (visual damage/stain analysis)
 *
 * ONNX model: ~14MB (v3 FP32), cached by browser after first load
 * ONNX Runtime Web: loaded from CDN (Microsoft, ~200KB WASM)
 */
(function () {
  "use strict";

  // ── Elements ──────────────────────────────────────────────────────
  var root = document.getElementById("lf-camera");
  if (!root) return;

  var proxyUrl = root.dataset.proxyUrl || "/apps/lithofinder";
  var lang = root.dataset.lang || "en";
  var country = root.dataset.country || "NL";
  var modelUrl = root.dataset.modelUrl || "";
  var rootUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/").replace(/\/$/, "");

  var modal = document.getElementById("lf-cam-modal");
  var openBtn = document.getElementById("lf-cam-open");
  var closeBtn = document.getElementById("lf-cam-close");
  var video = document.getElementById("lf-cam-video");
  var canvas = document.getElementById("lf-cam-canvas");
  var guide = document.getElementById("lf-cam-guide");
  var hint = document.getElementById("lf-cam-hint");
  var errorEl = document.getElementById("lf-cam-error");
  var errorMsg = document.getElementById("lf-cam-error-msg");
  var analyzingEl = document.getElementById("lf-cam-analyzing");
  var analyzingText = document.getElementById("lf-cam-analyzing-text");
  var controls = document.getElementById("lf-cam-controls");
  var captureBtn = document.getElementById("lf-cam-capture");
  var flipBtn = document.getElementById("lf-cam-flip");
  var uploadInput = document.getElementById("lf-cam-upload");
  var fallbackInput = document.getElementById("lf-cam-fallback");
  var results = document.getElementById("lf-cam-results");
  var resultTitle = document.getElementById("lf-cam-result-title");
  var confidenceEl = document.getElementById("lf-cam-confidence");
  var resultDetails = document.getElementById("lf-cam-result-details");
  var productsEl = document.getElementById("lf-cam-products");
  var retakeBtn = document.getElementById("lf-cam-retake");
  var modeBtns = root.querySelectorAll(".lf-cam__mode");

  // Localization
  var loc = document.getElementById("lf-cam-locale");
  var L = function (key) { return loc ? (loc.dataset[key] || key) : key; };

  // ── ONNX Model State ─────────────────────────────────────────────
  var onnxSession = null;
  var onnxLoading = false;
  var onnxReady = false;
  var onnxOutputNames = null;

  // Fase 6 (DECK-198): policy-laag-data (T_direct-drempels, subtype/surface-
  // klassenlijsten, tier-taxonomie), geladen naast het model als het een
  // v5-model is (3+ outputs). Blijft null bij het huidige interim-model
  // (fase 1, 1 output) - de policy-laag valt dan vanzelf terug op tier 4
  // (alleen-groep), precies het bestaande gedrag.
  var modelLabels = null;

  // MINC-2500 categories (alphabetical - matches model output indices)
  var CATEGORIES = [
    "brick", "carpet", "ceramic", "fabric", "foliage",
    "food", "glass", "hair", "leather", "metal",
    "mirror", "other", "painted", "paper", "plastic",
    "polishedstone", "skin", "sky", "stone", "tile",
    "wallpaper", "water", "wood"
  ];

  // Categories relevant to Lithofin products
  var LITHOFIN_RELEVANT = {
    brick: true, ceramic: true, glass: true,
    polishedstone: true, stone: true, tile: true
  };

  // MINC category -> Lithofin surface group
  var SURFACE_MAP = {
    brick: "cotto-terracotta",
    ceramic: "ceramics",
    glass: "ceramics",
    polishedstone: "natural-stone",
    stone: "natural-stone",
    tile: "ceramics"
  };

  // Lithofinder 2.0 (DECK-193): scan-log/consent-versionering. Ophogen bij
  // een materiele wijziging aan wat er gelogd/getoond wordt (niet bij een
  // tekstuele wijziging van de melding zelf).
  var MODEL_VERSION_FALLBACK = "minc23-interim-v1"; // huidige 23-klassenmodel, groep-only (fase 1)
  // Fase 6: zodra een v5-model + labels-v5.json geladen zijn, is de echte
  // versie afkomstig uit dat bestand (bv. "5.0") i.p.v. de hardcoded fallback.
  function getModelVersion() {
    return (modelLabels && modelLabels.version) ? "v" + modelLabels.version : MODEL_VERSION_FALLBACK;
  }
  var CONSENT_VERSION = "2026-07-v1";

  // ImageNet normalization (same as training)
  var NORM_MEAN = [0.485, 0.456, 0.406];
  var NORM_STD = [0.229, 0.224, 0.225];

  // ── Load ONNX Runtime Web (from CDN) ──────────────────────────────
  function loadORT() {
    return new Promise(function (resolve, reject) {
      if (window.ort) { resolve(window.ort); return; }

      var script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/ort.min.js";
      script.crossOrigin = "anonymous";
      script.onload = function () {
        if (window.ort) {
          window.ort.env.wasm.wasmPaths = "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/";
          // Force single-threaded WASM - required for iOS Safari (no SharedArrayBuffer)
          window.ort.env.wasm.numThreads = 1;
          window.ort.env.wasm.simd = true;
          resolve(window.ort);
        } else {
          reject(new Error("ORT loaded but window.ort not found"));
        }
      };
      script.onerror = function () { reject(new Error("Failed to load ONNX Runtime")); };
      document.head.appendChild(script);
    });
  }

  // ── Load ONNX Model ──────────────────────────────────────────────
  async function ensureModel() {
    if (onnxReady) return true;
    if (onnxLoading) {
      while (onnxLoading) await new Promise(function (r) { setTimeout(r, 100); });
      return onnxReady;
    }

    if (!modelUrl) {
      console.warn("[LF Camera] No model URL, falling back to Worker-only");
      return false;
    }

    onnxLoading = true;
    try {
      var ortLib = await loadORT();

      // Timeout: 45s for model download (14MB FP32 on mobile can be slow)
      var timeoutId;
      var timeoutPromise = new Promise(function (_, rej) {
        timeoutId = setTimeout(function () { rej(new Error("Model load timeout (45s)")); }, 45000);
      });
      onnxSession = await Promise.race([
        ortLib.InferenceSession.create(modelUrl, {
          executionProviders: ["wasm"],
          graphOptimizationLevel: "all",
        }),
        timeoutPromise
      ]);
      clearTimeout(timeoutId);
      onnxReady = true;
      onnxOutputNames = onnxSession.outputNames || null;

      // v5-model (fase 5/6): heeft een subtype_logits-output naast de oude
      // minc_logits. Haal dan de bijbehorende kalibratie/taxonomie op
      // (best-effort, non-blocking - faalt dit, dan blijft modelLabels null
      // en werkt de policy-laag verderop gewoon in tier-4-only-modus).
      if (onnxOutputNames && onnxOutputNames.indexOf("subtype_logits") !== -1) {
        try {
          var labelsUrl = modelUrl.replace(/[^/]+\.onnx(\?.*)?$/, "labels-v5.json");
          var labelsRes = await fetch(labelsUrl);
          if (labelsRes.ok) modelLabels = await labelsRes.json();
        } catch (e) {
          console.warn("[LF Camera] labels-v5.json kon niet geladen worden, policy-laag blijft op tier 4:", e.message);
        }
      }
    } catch (err) {
      console.warn("[LF Camera] ONNX load failed, using Worker fallback:", err.message);
      onnxReady = false;
    }
    onnxLoading = false;
    return onnxReady;
  }

  // ── Browser-side Inference (Stage 1) ──────────────────────────────
  function preprocessImage(imgElement) {
    var c = document.createElement("canvas");
    c.width = 224;
    c.height = 224;
    var ctx = c.getContext("2d");
    ctx.drawImage(imgElement, 0, 0, 224, 224);
    var imageData = ctx.getImageData(0, 0, 224, 224);
    var data = imageData.data;

    // Convert to CHW float32 tensor with ImageNet normalization
    var float32 = new Float32Array(1 * 3 * 224 * 224);
    for (var y = 0; y < 224; y++) {
      for (var x = 0; x < 224; x++) {
        var srcIdx = (y * 224 + x) * 4;
        var r = data[srcIdx] / 255.0;
        var g = data[srcIdx + 1] / 255.0;
        var b = data[srcIdx + 2] / 255.0;

        float32[0 * 224 * 224 + y * 224 + x] = (r - NORM_MEAN[0]) / NORM_STD[0];
        float32[1 * 224 * 224 + y * 224 + x] = (g - NORM_MEAN[1]) / NORM_STD[1];
        float32[2 * 224 * 224 + y * 224 + x] = (b - NORM_MEAN[2]) / NORM_STD[2];
      }
    }

    return new window.ort.Tensor("float32", float32, [1, 3, 224, 224]);
  }

  function softmax(logits) {
    var maxVal = -Infinity;
    var i;
    for (i = 0; i < logits.length; i++) {
      if (logits[i] > maxVal) maxVal = logits[i];
    }
    var sum = 0;
    var probs = new Float32Array(logits.length);
    for (i = 0; i < logits.length; i++) {
      probs[i] = Math.exp(logits[i] - maxVal);
      sum += probs[i];
    }
    for (i = 0; i < probs.length; i++) {
      probs[i] /= sum;
    }
    return probs;
  }

  // ── Multi-crop preprocessing for TTA (Test-Time Augmentation) ──────
  function preprocessCrops(imgElement) {
    // Returns array of tensors: [center-224, center-flipped, zoomed-center]
    // This makes predictions much more stable than a single crop
    var crops = [];
    var w = imgElement.width || imgElement.videoWidth || 224;
    var h = imgElement.height || imgElement.videoHeight || 224;

    // Crop configs: [sx, sy, sw, sh, flipH]
    var configs = [
      // Center crop (standard)
      { sx: 0, sy: 0, sw: w, sh: h, flip: false },
      // Center crop flipped
      { sx: 0, sy: 0, sw: w, sh: h, flip: true },
      // Zoomed center (inner 70%) - closer look at texture
      { sx: Math.round(w * 0.15), sy: Math.round(h * 0.15),
        sw: Math.round(w * 0.7), sh: Math.round(h * 0.7), flip: false },
    ];

    for (var ci = 0; ci < configs.length; ci++) {
      var cfg = configs[ci];
      var c = document.createElement("canvas");
      c.width = 224; c.height = 224;
      var ctx = c.getContext("2d");
      if (cfg.flip) {
        ctx.translate(224, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(imgElement, cfg.sx, cfg.sy, cfg.sw, cfg.sh, 0, 0, 224, 224);
      var imageData = ctx.getImageData(0, 0, 224, 224);
      var data = imageData.data;

      var float32 = new Float32Array(1 * 3 * 224 * 224);
      for (var y = 0; y < 224; y++) {
        for (var x = 0; x < 224; x++) {
          var srcIdx = (y * 224 + x) * 4;
          var r = data[srcIdx] / 255.0;
          var g = data[srcIdx + 1] / 255.0;
          var b = data[srcIdx + 2] / 255.0;
          float32[0 * 224 * 224 + y * 224 + x] = (r - NORM_MEAN[0]) / NORM_STD[0];
          float32[1 * 224 * 224 + y * 224 + x] = (g - NORM_MEAN[1]) / NORM_STD[1];
          float32[2 * 224 * 224 + y * 224 + x] = (b - NORM_MEAN[2]) / NORM_STD[2];
        }
      }
      crops.push(new window.ort.Tensor("float32", float32, [1, 3, 224, 224]));
    }
    return crops;
  }

  function argmax(probs) {
    var topIdx = 0;
    for (var i = 1; i < probs.length; i++) {
      if (probs[i] > probs[topIdx]) topIdx = i;
    }
    return topIdx;
  }

  // Top-N indices van een probs-array, gesorteerd hoog->laag.
  function topN(probs, n) {
    var idx = [];
    for (var i = 0; i < probs.length; i++) idx.push(i);
    idx.sort(function (a, b) { return probs[b] - probs[a]; });
    return idx.slice(0, n);
  }

  async function classifyLocal(blob, useTTA) {
    var img = await new Promise(function (resolve, reject) {
      var image = new Image();
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error("Failed to load image")); };
      image.src = URL.createObjectURL(blob);
    });

    var isV5 = onnxOutputNames && onnxOutputNames.indexOf("subtype_logits") !== -1;
    var tensors;
    if (useTTA) {
      tensors = preprocessCrops(img);
      URL.revokeObjectURL(img.src);
    } else {
      tensors = [preprocessImage(img)];
      URL.revokeObjectURL(img.src);
    }

    if (!isV5) {
      // ── Interim-model (fase 1): 1 output, alleen MINC-23 + groep-mapping ──
      var allProbsInterim = [];
      for (var ci0 = 0; ci0 < tensors.length; ci0++) {
        var out0 = await onnxSession.run({ input: tensors[ci0] });
        allProbsInterim.push(softmax(out0.output.data));
      }
      var avgProbsInterim = new Float32Array(CATEGORIES.length);
      for (var i0 = 0; i0 < CATEGORIES.length; i0++) {
        var sum0 = 0;
        for (var j0 = 0; j0 < allProbsInterim.length; j0++) sum0 += allProbsInterim[j0][i0];
        avgProbsInterim[i0] = sum0 / allProbsInterim.length;
      }
      var topIdx0 = argmax(avgProbsInterim);
      var category0 = CATEGORIES[topIdx0];
      return {
        minc_category: category0,
        confidence: avgProbsInterim[topIdx0],
        is_lithofin_relevant: LITHOFIN_RELEVANT[category0] || false,
        surface_id: SURFACE_MAP[category0] || null,
        subtype_id: null,
        subtype_confidence: 0,
        surface_confidence: 0,
        top3_subtypes: [],
        _stage1_local: true
      };
    }

    // ── v5-model (fase 6): 3 heads + embedding ──────────────────────────
    var mincProbsAll = [], surfProbsAll = [], subProbsAll = [];
    for (var ci = 0; ci < tensors.length; ci++) {
      var output = await onnxSession.run({ input: tensors[ci] });
      mincProbsAll.push(softmax(output.minc_logits.data));
      surfProbsAll.push(softmax(output.surface_logits.data));
      subProbsAll.push(softmax(output.subtype_logits.data));
    }
    function avg(list, len) {
      var out = new Float32Array(len);
      for (var i = 0; i < len; i++) {
        var s = 0;
        for (var j = 0; j < list.length; j++) s += list[j][i];
        out[i] = s / list.length;
      }
      return out;
    }
    var mincClasses = modelLabels.minc_classes || CATEGORIES;
    var surfaceGroups = modelLabels.surface_groups || [];
    var subtypeClasses = modelLabels.subtype_classes || [];

    var mincProbs = avg(mincProbsAll, mincClasses.length);
    var surfProbs = avg(surfProbsAll, surfaceGroups.length);
    var subProbs = avg(subProbsAll, subtypeClasses.length);

    var mincTop = argmax(mincProbs);
    var mincCategory = mincClasses[mincTop];
    var surfTop = argmax(surfProbs);
    var subTop = argmax(subProbs);
    var subtypeId = subtypeClasses[subTop];

    // Kalibratie toepassen indien geladen (temperature-scaling, fase 5/6).
    var calib = modelLabels.calibration || null;
    var subConfidence = subProbs[subTop];
    if (calib && calib.temperature && calib.temperature.subtype) {
      // De confidences komen al uit een softmax; temperature is al verwerkt
      // in de export als het model met kalibratie is geëxporteerd. Bewaar
      // de rauwe waarde hier, geen dubbele scaling.
    }

    var top3Idx = topN(subProbs, 3);
    var top3 = top3Idx.map(function (idx) {
      return { subtype_id: subtypeClasses[idx], confidence: subProbs[idx] };
    });

    return {
      minc_category: mincCategory,
      confidence: mincProbs[mincTop],
      is_lithofin_relevant: LITHOFIN_RELEVANT[mincCategory] || false,
      surface_id: surfaceGroups[surfTop] || null,
      surface_confidence: surfProbs[surfTop],
      subtype_id: subtypeId,
      subtype_confidence: subConfidence,
      top3_subtypes: top3,
      _stage1_local: true,
      _model_v5: true
    };
  }

  // ── State ─────────────────────────────────────────────────────────
  var stream = null;
  var facingMode = "environment";
  var currentMode = "surface";
  var lastRecognitionId = null;
  var lastPrediction = null;
  var lastCapturedBlob = null;
  var autoDetectTimer = null;
  var autoDetectRunning = false;

  // ── Label refinements (loaded from feedback data) ──────────────────
  // Maps "source:predicted" → { label, subtype, count }
  var labelRefinements = null;
  var refinementsLoading = false;

  // Live detection overlay elements
  var liveOverlay = document.getElementById("lf-cam-live");
  var liveLabel = document.getElementById("lf-cam-live-label");
  var liveConf = document.getElementById("lf-cam-live-conf");
  var liveDot = liveOverlay ? liveOverlay.querySelector(".lf-cam__live-dot") : null;
  var liveUpBtn = document.getElementById("lf-cam-live-up");
  var liveDownBtn = document.getElementById("lf-cam-live-down");

  // Live vote: upvote = confirm live detection, capture screenshot + send feedback
  if (liveUpBtn) {
    liveUpBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      liveUpBtn.classList.add("active", "lf-cam__live-vote--flash");
      setTimeout(function () { liveUpBtn.classList.remove("active", "lf-cam__live-vote--flash"); }, 800);
      // Capture current frame as screenshot
      if (video.videoWidth) {
        canvas.width = Math.min(video.videoWidth, 512);
        canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(function (blob) {
          if (!blob) return;
          lastCapturedBlob = blob;
          var liveText = liveLabel ? liveLabel.textContent : "";
          var fd = new FormData();
          fd.append("recognition_id", "live_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6));
          fd.append("vote", "1");
          fd.append("mode", currentMode);
          fd.append("predicted_name", streakCategory || liveText);
          fd.append("stage1_source", "browser-onnx-live");
          fd.append("predicted_surface_id", SURFACE_MAP[streakCategory] || "");
          fd.append("image", blob, "live.jpg");
          fd.append("locale", lang);
          fetch(proxyUrl + "/feedback", { method: "POST", body: fd });
        }, "image/jpeg", 0.8);
      }
    });
  }

  // Live vote: downvote = "this is wrong" → immediately capture and full-analyze
  if (liveDownBtn) {
    liveDownBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      liveDownBtn.classList.add("active", "lf-cam__live-vote--flash");
      setTimeout(function () { liveDownBtn.classList.remove("active", "lf-cam__live-vote--flash"); }, 800);
      // Force capture and analyze (bypass ONNX gate)
      captureAndAnalyze(true);
    });
  }

  // Pre-load ONNX model in background (non-blocking)
  ensureModel();

  // ── Open / Close Modal ────────────────────────────────────────────
  openBtn.addEventListener("click", openCamera);
  closeBtn.addEventListener("click", closeCamera);

  var heroPhotoBtn = document.getElementById("lf-hero-photo-btn");
  if (heroPhotoBtn) {
    heroPhotoBtn.addEventListener("click", function (e) {
      e.preventDefault();
      openCamera();
    });
  }

  modal.addEventListener("click", function (e) {
    if (e.target === modal) closeCamera();
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && modal.style.display !== "none") closeCamera();
  });

  // ── Load label refinements from feedback data ────────────────────
  function loadRefinements() {
    if (labelRefinements || refinementsLoading) return;
    refinementsLoading = true;
    fetch(proxyUrl + "/feedback/refinements")
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (data) { labelRefinements = data || {}; })
      .catch(function () { labelRefinements = {}; })
      .finally(function () { refinementsLoading = false; });
  }

  function openCamera() {
    modal.style.display = "flex";
    document.body.style.overflow = "hidden";
    resetToViewfinder();
    startStream();
    loadRefinements(); // fetch correction patterns in background
  }

  function closeCamera() {
    modal.style.display = "none";
    document.body.style.overflow = "";
    stopStream();
  }

  // ── Camera Stream ─────────────────────────────────────────────────
  async function startStream() {
    errorEl.style.display = "none";
    video.style.display = "block";

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showError(L("errorNotSupported"));
      return;
    }

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      await video.play();

      // iOS Safari: video.play() resolves before frames are available
      // Wait for actual video data before starting auto-detect
      if (video.videoWidth === 0 || video.readyState < 2) {
        await new Promise(function (resolve) {
          var done = false;
          function onReady() {
            if (done) return; done = true;
            video.removeEventListener("loadeddata", onReady);
            resolve();
          }
          video.addEventListener("loadeddata", onReady);
          // Fallback: don't wait forever
          setTimeout(function () { if (!done) { done = true; resolve(); } }, 4000);
        });
      }
      startAutoDetect();
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        showError(L("errorPermission"));
      } else {
        showError(L("errorGeneric"));
      }
    }
  }

  function stopStream() {
    stopAutoDetect();
    if (stream) {
      stream.getTracks().forEach(function (t) { t.stop(); });
      stream = null;
    }
    video.srcObject = null;
  }

  function showError(msg) {
    video.style.display = "none";
    guide.style.display = "none";
    errorEl.style.display = "flex";
    errorMsg.textContent = msg;
  }

  // ── Flip Camera ───────────────────────────────────────────────────
  flipBtn.addEventListener("click", function () {
    facingMode = facingMode === "environment" ? "user" : "environment";
    stopStream();
    startStream();
  });

  // ── Mode Switching ────────────────────────────────────────────────
  modeBtns.forEach(function (btn) {
    btn.addEventListener("click", function () {
      modeBtns.forEach(function (b) { b.classList.remove("lf-cam__mode--active"); });
      btn.classList.add("lf-cam__mode--active");
      currentMode = btn.dataset.mode;
      hint.textContent = currentMode === "problem" ? L("hintProblem") : L("hintSurface");
      // Show/hide live detection based on mode
      if (currentMode === "problem") {
        stopAutoDetect();
      } else if (stream) {
        startAutoDetect();
      }
    });
  });

  // ── Smart Auto-Detection (ONNX, 0 API cost) ───────────────────────
  //
  // Prediction smoothing: keeps a sliding window of the last N frames.
  // Shows consensus category (most frequent + weighted by confidence).
  // Auto-captures when the same relevant material is stable for K frames.
  //
  var autoDetectRetries = 0;
  var AUTO_DETECT_MAX_RETRIES = 5;
  var SMOOTH_WINDOW = 5;           // frames to average
  var AUTO_CAPTURE_STREAK = 5;     // stable relevant frames to trigger capture
  var AUTO_CAPTURE_MIN_CONF = 0.65; // minimum avg confidence for auto-capture
  var INTERVAL_RELEVANT = 700;     // ms between scans when relevant surface seen
  var INTERVAL_OTHER = 1800;       // ms between scans otherwise
  var frameHistory = [];           // [{category, confidence, isRelevant}]
  var streakCategory = null;       // current streak category
  var streakCount = 0;             // consecutive frames with same relevant cat
  var autoCaptureTriggered = false;

  // Progress ring for auto-capture (SVG circle around capture button)
  var captureRingProgress = null;
  function initCaptureProgress() {
    if (captureRingProgress) return;
    var btn = captureBtn;
    if (!btn) return;
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "lf-cam__auto-ring");
    svg.setAttribute("viewBox", "0 0 72 72");
    svg.setAttribute("width", "72");
    svg.setAttribute("height", "72");
    var circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    circle.setAttribute("cx", "36");
    circle.setAttribute("cy", "36");
    circle.setAttribute("r", "33");
    circle.setAttribute("fill", "none");
    circle.setAttribute("stroke", "#4ade80");
    circle.setAttribute("stroke-width", "3");
    circle.setAttribute("stroke-dasharray", String(Math.PI * 66));
    circle.setAttribute("stroke-dashoffset", String(Math.PI * 66));
    circle.setAttribute("stroke-linecap", "round");
    circle.setAttribute("transform", "rotate(-90 36 36)");
    circle.style.transition = "stroke-dashoffset 0.4s ease";
    svg.appendChild(circle);
    btn.parentNode.style.position = "relative";
    btn.insertAdjacentElement("afterend", svg);
    captureRingProgress = circle;
  }

  function setCaptureProgress(fraction) {
    if (!captureRingProgress) initCaptureProgress();
    if (!captureRingProgress) return;
    var circumference = Math.PI * 66;
    captureRingProgress.setAttribute("stroke-dashoffset", String(circumference * (1 - fraction)));
    // Green when close, dimmer when far
    captureRingProgress.setAttribute("stroke", fraction >= 0.75 ? "#22c55e" : "#4ade80");
  }

  function resetCaptureProgress() {
    if (captureRingProgress) {
      var circumference = Math.PI * 66;
      captureRingProgress.style.transition = "none";
      captureRingProgress.setAttribute("stroke-dashoffset", String(circumference));
      // Force reflow then restore transition
      captureRingProgress.getBoundingClientRect();
      captureRingProgress.style.transition = "stroke-dashoffset 0.4s ease";
    }
  }

  function getConsensus() {
    if (frameHistory.length === 0) return null;
    // Weighted vote: each frame votes for its category weighted by confidence
    var votes = {};
    for (var i = 0; i < frameHistory.length; i++) {
      var f = frameHistory[i];
      votes[f.category] = (votes[f.category] || 0) + f.confidence;
    }
    var best = null, bestScore = 0;
    for (var cat in votes) {
      if (votes[cat] > bestScore) { bestScore = votes[cat]; best = cat; }
    }
    // Average confidence for the winning category
    var count = 0, sum = 0;
    for (var j = 0; j < frameHistory.length; j++) {
      if (frameHistory[j].category === best) { sum += frameHistory[j].confidence; count++; }
    }
    return {
      category: best,
      confidence: count > 0 ? sum / count : 0,
      isRelevant: LITHOFIN_RELEVANT[best] || false,
      votes: count,
      totalFrames: frameHistory.length
    };
  }

  function startAutoDetect() {
    if (autoDetectRunning) return;
    autoDetectRunning = true;
    autoDetectRetries = 0;
    autoCaptureTriggered = false;
    frameHistory = [];
    streakCategory = null;
    streakCount = 0;
    resetCaptureProgress();
    runAutoDetectLoop();
  }

  function stopAutoDetect() {
    autoDetectRunning = false;
    if (autoDetectTimer) { clearTimeout(autoDetectTimer); autoDetectTimer = null; }
    if (liveOverlay) liveOverlay.style.display = "none";
    resetCaptureProgress();
  }

  async function runAutoDetectLoop() {
    if (!autoDetectRunning || !stream || currentMode !== "surface") return;

    // Wait for model to be ready
    if (!onnxReady) {
      if (liveOverlay && liveLabel) {
        liveLabel.textContent = L("loadingModel");
        liveConf.textContent = "";
        liveOverlay.style.display = "flex";
        if (liveDot) { liveDot.classList.remove("lf-cam__live-dot--irrelevant"); liveDot.classList.add("lf-cam__live-dot--loading"); }
      }
      var ready = await ensureModel();
      if (!ready) {
        autoDetectRetries++;
        console.warn("[LF Camera] Model load attempt", autoDetectRetries, "/", AUTO_DETECT_MAX_RETRIES);
        if (autoDetectRetries >= AUTO_DETECT_MAX_RETRIES) {
          if (liveLabel) liveLabel.textContent = L("tapCapture");
          if (liveConf) liveConf.textContent = "";
          if (liveDot) { liveDot.classList.remove("lf-cam__live-dot--loading"); liveDot.classList.add("lf-cam__live-dot--irrelevant"); }
          return;
        }
        if (liveLabel) liveLabel.textContent = L("loadingModel") + " (" + autoDetectRetries + "/" + AUTO_DETECT_MAX_RETRIES + ")";
        if (autoDetectRunning) autoDetectTimer = setTimeout(runAutoDetectLoop, 3000);
        return;
      }
      if (liveDot) liveDot.classList.remove("lf-cam__live-dot--loading");
    }

    var nextInterval = INTERVAL_OTHER;

    try {
      if (video.videoWidth > 0 && video.readyState >= 2) {
        var c = document.createElement("canvas");
        c.width = 224; c.height = 224;
        c.getContext("2d").drawImage(video, 0, 0, 224, 224);
        var blob = await new Promise(function (r) {
          try {
            c.toBlob(function (b) {
              if (b) { r(b); return; }
              var dataUrl = c.toDataURL("image/jpeg", 0.6);
              fetch(dataUrl).then(function (res) { return res.blob(); }).then(r).catch(function () { r(null); });
            }, "image/jpeg", 0.6);
          } catch (e) { r(null); }
        });

        if (blob && autoDetectRunning) {
          var result = await classifyLocal(blob, false);  // fast: no TTA

          // Push to sliding window
          frameHistory.push({
            category: result.minc_category,
            confidence: result.confidence,
            isRelevant: result.is_lithofin_relevant
          });
          if (frameHistory.length > SMOOTH_WINDOW) frameHistory.shift();

          // Get smoothed consensus
          var consensus = getConsensus();
          if (!consensus || !autoDetectRunning) return;

          var name = smartLabel(consensus.category);
          var pct = Math.round(consensus.confidence * 100);

          // Update streak tracking
          if (consensus.isRelevant && consensus.category === streakCategory) {
            streakCount++;
          } else {
            streakCategory = consensus.category;
            streakCount = consensus.isRelevant ? 1 : 0;
          }

          // Update live overlay
          if (liveOverlay) {
            liveLabel.textContent = name;
            liveConf.textContent = pct + "%";
            liveOverlay.style.display = "flex";
            if (liveDot) {
              liveDot.classList.remove("lf-cam__live-dot--irrelevant", "lf-cam__live-dot--loading", "lf-cam__live-dot--locking");
              if (!consensus.isRelevant) {
                liveDot.classList.add("lf-cam__live-dot--irrelevant");
              } else if (streakCount >= 2) {
                liveDot.classList.add("lf-cam__live-dot--locking");
              }
            }
          }

          // Update hint + capture progress based on state
          if (consensus.isRelevant) {
            nextInterval = INTERVAL_RELEVANT; // scan faster

            if (streakCount >= 2 && consensus.confidence >= AUTO_CAPTURE_MIN_CONF) {
              // Show progress toward auto-capture
              var progress = Math.min(streakCount / AUTO_CAPTURE_STREAK, 1);
              setCaptureProgress(progress);
              hint.textContent = "\u2714 " + name + " - " + L("holdSteady");
              hint.style.background = "rgba(26, 92, 58, 0.8)";
            } else {
              resetCaptureProgress();
              hint.textContent = "\u2714 " + name + " - " + L("tapCapture");
              hint.style.background = "rgba(26, 92, 58, 0.7)";
            }

            // Auto-capture when stable + confident
            if (streakCount >= AUTO_CAPTURE_STREAK
                && consensus.confidence >= AUTO_CAPTURE_MIN_CONF
                && !autoCaptureTriggered) {
              autoCaptureTriggered = true;
              // Brief visual flash before capture
              if (captureBtn) captureBtn.classList.add("lf-cam__btn-capture--flash");
              setTimeout(function () {
                if (captureBtn) captureBtn.classList.remove("lf-cam__btn-capture--flash");
              }, 300);
              captureAndAnalyze(false);
              return; // stop loop - captureAndAnalyze will handle
            }
          } else {
            resetCaptureProgress();
            streakCount = 0;
            hint.textContent = name + " - " + L("notStone");
            hint.style.background = "rgba(180, 80, 0, 0.6)";
          }
        }
      }
    } catch (e) {
      console.warn("[LF Camera] Auto-detect frame error:", e.message);
    }

    if (autoDetectRunning) {
      autoDetectTimer = setTimeout(runAutoDetectLoop, nextInterval);
    }
  }

  // ── Capture ───────────────────────────────────────────────────────
  captureBtn.addEventListener("click", function () { captureAndAnalyze(true); });

  async function captureAndAnalyze(manual) {
    if (!video.videoWidth) return;
    stopAutoDetect();

    canvas.width = Math.min(video.videoWidth, 1024);
    canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
    var ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    var blob = await new Promise(function (resolve) {
      canvas.toBlob(function (b) {
        if (b) { resolve(b); return; }
        // iOS Safari fallback
        var dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        fetch(dataUrl).then(function (r) { return r.blob(); }).then(resolve);
      }, "image/jpeg", 0.85);
    });

    lastCapturedBlob = blob;
    await analyzeImage(blob, manual !== false);
  }

  // ── File Upload ───────────────────────────────────────────────────
  uploadInput.addEventListener("change", handleFileUpload);
  fallbackInput.addEventListener("change", handleFileUpload);

  async function handleFileUpload(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = "";
    var blob = await resizeImage(file, 1024);
    lastCapturedBlob = blob;
    await analyzeImage(blob, true);
  }

  // ── Analyze Image (Lithofinder 2.0: 100% on-device, 0 AI-tokens) ──
  // manualCapture: true when user pressed button or uploaded photo (bypass ONNX gate)
  //
  // Sinds fase 1 (DECK-193) bestaat er GEEN server-side classificatie meer:
  // stone-recognize.ts/problem-recognize.ts (OpenAI) zijn verwijderd. De
  // browser doet de hele herkenning (classifyLocal, hieronder); de server
  // levert alleen nog token-vrije product/kennis-verrijking (/enrich) en
  // logt de scan (met melding) als trainingsdata voor latere hertraining
  // (fase 4-7). Zie ixstone/docs/lithofinder-2.0.md.

  async function analyzeImage(blob, manualCapture) {
    guide.style.display = "none";
    controls.style.display = "none";
    results.style.display = "none";
    analyzingEl.style.display = "flex";

    try {
      if (currentMode === "problem") {
        analyzingText.textContent = L("analyzingProblem");
        await analyzeProblem(blob);
        return;
      }

      analyzingText.textContent = L("analyzing");
      await analyzeSurface(blob, manualCapture);
    } catch (err) {
      analyzingEl.style.display = "none";
      guide.style.display = "flex";
      controls.style.display = "flex";
      alert((err && err.message) || L("errorGeneric"));
    }
  }

  // ── Surface analysis: 100% on-device ──────────────────────────────
  // manualCapture = true when user pressed capture button or uploaded a photo
  // (dan tonen we altijd een resultaat/kiezer, ook als het model onzeker is).
  async function analyzeSurface(blob, manualCapture) {
    var t0 = (window.performance && performance.now) ? performance.now() : Date.now();
    var modelReady = await ensureModel();

    if (!modelReady) {
      // Model kon niet laden (timeout/netwerk) -> nette wizard-fallback,
      // geen dode kaart. Zie plan A2 regel 5.
      showWizardFallback(blob);
      return;
    }

    var stage1 = await classifyLocal(blob, true);
    var latencyMs = Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - t0);

    if (!stage1.is_lithofin_relevant && !manualCapture) {
      var irrelevantData = {
        stone_type: humanName(stage1.minc_category),
        minc_category: stage1.minc_category,
        surface_id: null,
        subtype_id: null,
        confidence: stage1.confidence,
        is_lithofin_relevant: false,
        recommended_products: [],
        _stage: "browser-only"
      };
      lastPrediction = irrelevantData;
      logScan({
        mode: "surface",
        model_version: getModelVersion(),
        backend: "wasm",
        latency_ms: latencyMs,
        predicted_surface_id: null,
        confidence: stage1.confidence,
        relevance: "irrelevant"
      }, null); // geen beeld bewaren voor duidelijk irrelevante scans
      showResults(irrelevantData);
      return;
    }

    var surfaceId = stage1.surface_id;
    var relevance = stage1.is_lithofin_relevant ? "relevant" : "uncertain";

    lastRecognitionId = newScanId();
    logScan({
      scan_id: lastRecognitionId,
      mode: "surface",
      model_version: getModelVersion(),
      backend: "wasm",
      latency_ms: latencyMs,
      predicted_surface_id: surfaceId,
      confidence: stage1.confidence,
      relevance: relevance
    }, blob);

    if (!surfaceId) {
      // Manual capture maar model kende de categorie niet als bekende groep -
      // laat de gebruiker toch zelf de groep + het type kiezen (nooit doodlopen).
      showGroupPicker(null, stage1);
      return;
    }

    // ── Fase 6 policy-laag (plan A2, tiers 2-3) ─────────────────────────
    // Alleen actief met een v5-model + geladen kalibratie (labels-v5.json).
    // Zonder die twee (het huidige interim-model) valt dit door naar het
    // bestaande tier-4-gedrag hieronder - geen enkel gedragsverschil voor
    // de nu live staande deployment.
    if (stage1._model_v5 && modelLabels && stage1.subtype_id) {
      var directThresholds = (modelLabels.calibration && modelLabels.calibration.direct_eligible_thresholds) || {};
      var directThreshold = directThresholds[stage1.subtype_id];
      if (directThreshold != null && stage1.subtype_confidence >= directThreshold) {
        await showDirectAnswer(stage1, surfaceId);
        return;
      }
      if (stage1.surface_confidence >= 0.65 && stage1.top3_subtypes && stage1.top3_subtypes.length >= 2) {
        await showTop3Assist(stage1, surfaceId);
        return;
      }
    }

    // Groep bekend, subtype nog niet: toon direct de groep-brede kaart
    // (producten voor de hele groep) EN de type-kiezer eronder (plan A2 - "alleen-groep").
    var enrichResult = await callEnrich({ mode: "surface", surface_id: surfaceId });
    var data = Object.assign({
      stone_type: humanName(stage1.minc_category),
      minc_category: stage1.minc_category,
      surface_id: surfaceId,
      subtype_id: null,
      confidence: stage1.confidence,
      is_lithofin_relevant: true,
      _stage: "browser-group"
    }, enrichResult || {});
    lastPrediction = data;
    showResults(data);
    showGroupPicker(surfaceId, stage1);
  }

  // ── Problem mode: dag-1 = visuele chip-kiezer, geen model (plan A3) ──
  async function analyzeProblem(blob) {
    var surfaceHint = lastPrediction && lastPrediction.surface_id ? lastPrediction.surface_id : null;
    var t0 = (window.performance && performance.now) ? performance.now() : Date.now();

    lastRecognitionId = newScanId();
    logScan({
      scan_id: lastRecognitionId,
      mode: "problem",
      model_version: "chip-picker-v1",
      backend: null,
      latency_ms: Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - t0),
      predicted_surface_id: surfaceHint,
      relevance: "uncertain"
    }, blob);

    analyzingEl.style.display = "none";
    results.style.display = "block";
    resultTitle.textContent = L("whichProblem") || "Wat is het probleem?";
    confidenceEl.innerHTML = "";
    productsEl.innerHTML = "";
    renderProblemPicker(surfaceHint);
  }

  // ── Enrich-call (token-vrij: pure data-lookup, geen AI) ───────────
  async function callEnrich(payload) {
    try {
      var res = await fetch(proxyUrl + "/enrich", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  // ── Scan-log (fire-and-forget, NA het tonen van het resultaat) ───
  function newScanId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
    return "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function logScan(fields, blob) {
    try {
      var fd = new FormData();
      Object.keys(fields).forEach(function (k) {
        var v = fields[k];
        if (v === null || v === undefined) return;
        fd.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
      });
      fd.append("locale", lang);
      fd.append("consent_version", CONSENT_VERSION);
      if (blob) fd.append("image", blob, "scan.jpg");
      fetch(proxyUrl + "/scan-log", { method: "POST", body: fd, keepalive: true }).catch(function () {});
    } catch (e) { /* logging mag nooit de UX breken */ }
  }

  function patchScanLabel(scanId, chosenLabel, labelSource) {
    if (!scanId) return;
    try {
      fetch(proxyUrl + "/scan-label", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scan_id: scanId, chosen_label: chosenLabel, label_source: labelSource }),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* best effort */ }
  }

  // ── Wizard-fallback (model kon niet laden) ────────────────────────
  function showWizardFallback(blob) {
    // Log deze poging ook (met een verse id) zodat een latere groepskeuze
    // via renderGroupChips een echte rij patcht i.p.v. een eventueel stale
    // lastRecognitionId van een vorige, ongerelateerde scan.
    lastRecognitionId = newScanId();
    logScan({
      scan_id: lastRecognitionId,
      mode: "surface",
      model_version: "model-load-failed",
      backend: null,
      relevance: "uncertain"
    }, blob || null);

    analyzingEl.style.display = "none";
    results.style.display = "block";
    controls.style.display = "none";
    resultTitle.textContent = L("wizardFallbackTitle") || L("whatIsIt");
    confidenceEl.innerHTML = "";
    resultDetails.innerHTML = '<p>' + (L("wizardFallbackHint") || "") + '</p>';
    productsEl.innerHTML = "";
    renderGroupChips(null);
  }

  // ── Human-readable category names ─────────────────────────────────
  function humanName(category) {
    var names = {
      brick: "Brick / Clinker",
      carpet: "Carpet / Textile",
      ceramic: "Ceramic",
      fabric: "Fabric / Textile",
      foliage: "Foliage / Plants",
      food: "Food",
      glass: "Glass",
      hair: "Hair",
      leather: "Leather",
      metal: "Metal",
      mirror: "Mirror / Glass",
      other: "Unknown Material",
      painted: "Painted Surface",
      paper: "Paper",
      plastic: "Plastic",
      polishedstone: "Marble / Granite",
      skin: "Skin",
      sky: "Sky",
      stone: "Natural Stone",
      tile: "Ceramic / Cotto",
      wallpaper: "Wallpaper",
      water: "Water",
      wood: "Wood"
    };
    return names[category] || category;
  }

  // ── Smart live label - uses feedback refinements when available ───
  // Checks if accumulated corrections suggest a more specific label
  // e.g., ONNX says "tile" but 8 users corrected to "Cotto floor tile"
  function smartLabel(category) {
    if (labelRefinements) {
      // Try source-specific key first, then generic
      var keys = [
        "browser-onnx-live:" + category,
        "browser-onnx:" + category,
        "unknown:" + category,
      ];
      for (var i = 0; i < keys.length; i++) {
        var ref = labelRefinements[keys[i]];
        if (ref && ref.count >= 2) {
          // Show refined label with a subtle indicator
          return ref.label;
        }
      }
    }
    return humanName(category);
  }

  // ── Show Results ──────────────────────────────────────────────────
  function showResults(data) {
    analyzingEl.style.display = "none";
    video.style.display = "none";
    results.style.display = "block";
    controls.style.display = "none";

    emitResult(data);

    if (currentMode === "surface") {
      showSurfaceResults(data);
    } else {
      showProblemResults(data);
    }
  }

  function showSurfaceResults(data) {
    resultTitle.textContent = data.stone_type || "Unknown";
    showConfidence(data.confidence);

    var html = "";

    // AI reasoning - show why it chose this classification
    if (data.reasoning) {
      html += '<div class="lf-cam__reasoning">';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg> ';
      html += "<span>" + esc(data.reasoning) + "</span></div>";
    }

    // Alternative suggestion when uncertain
    if (data.alternative && data.alternative.subtype_id) {
      var altName = (data.alternative.info && data.alternative.info.name) ? data.alternative.info.name : data.alternative.subtype_id;
      html += '<div class="lf-cam__alternative" id="lf-cam-alt">';
      html += '<span class="lf-cam__alt-label">' + L("orMaybe") + "</span>";
      html += '<button class="lf-cam__alt-btn" type="button" data-alt-subtype="' + esc(data.alternative.subtype_id) + '">';
      html += esc(altName);
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>';
      html += "</button></div>";
    }

    if (data.characteristics && data.characteristics.length) {
      html += '<div class="lf-cam__detail-section">';
      html += "<h5>" + L("characteristics") + "</h5><ul>";
      data.characteristics.forEach(function (c) { html += "<li>" + esc(c) + "</li>"; });
      html += "</ul></div>";
    }

    if (data.care_warnings && data.care_warnings.length) {
      html += '<div class="lf-cam__detail-section lf-cam__detail-section--warn">';
      html += "<h5>" + L("careWarnings") + "</h5><ul>";
      data.care_warnings.forEach(function (w) { html += "<li>" + esc(w) + "</li>"; });
      html += "</ul></div>";
    }

    if (data.is_lithofin_relevant === false) {
      html += '<div class="lf-cam__not-relevant"><p>' + L("notRelevant") + "</p></div>";
    }

    if (data.surface_id) {
      html += '<div class="lf-cam__badges">';
      html += '<span class="lf-cam__badge">' + esc(data.surface_id) + "</span>";
      if (data.subtype_id) html += '<span class="lf-cam__badge lf-cam__badge--sub">' + esc(data.subtype_id) + "</span>";
      if (data.likely_application) html += '<span class="lf-cam__badge lf-cam__badge--app">' + esc(data.likely_application) + "</span>";
      html += "</div>";
    }

    if (data._stage === "browser-only") {
      html += '<div class="lf-cam__badges"><span class="lf-cam__badge lf-cam__badge--fast">instant</span></div>';
    }

    // Knowledge links
    if (data.subtype_id) {
      var kl = (window.LithofinderData && window.LithofinderData.knowledgeLinks) ? window.LithofinderData.knowledgeLinks[data.subtype_id] : null;
      html += '<div class="lf-cam__knowledge">';
      html += '<a class="lf-cam__klink" href="' + rootUrl + '/pages/stone-' + esc(data.subtype_id) + '">';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg> ';
      html += L("learnAbout") + " " + esc(data.stone_type) + "</a>";
      if (kl && kl.care && kl.care.length) {
        html += '<div class="lf-cam__kgroup"><h6>' + L("careGuides") + "</h6>";
        kl.care.forEach(function (c) {
          html += '<a class="lf-cam__kpill" href="' + rootUrl + '/pages/' + esc(c.h) + '">' + esc(c.t) + "</a>";
        });
        html += "</div>";
      }
      if (kl && kl.recipes && kl.recipes.length) {
        html += '<div class="lf-cam__kgroup"><h6>' + L("recipes") + "</h6>";
        kl.recipes.forEach(function (r) {
          html += '<a class="lf-cam__kpill lf-cam__kpill--recipe" href="' + rootUrl + '/pages/' + esc(r.h) + '">' + esc(r.t) + "</a>";
        });
        html += "</div>";
      }
      if (kl && kl.cases && kl.cases.length) {
        html += '<div class="lf-cam__kgroup"><h6>' + L("cases") + "</h6>";
        kl.cases.forEach(function (c) {
          html += '<a class="lf-cam__kpill lf-cam__kpill--case" href="' + rootUrl + '/pages/' + esc(c.h) + '">' + esc(c.t) + "</a>";
        });
        html += "</div>";
      }
      html += "</div>";
    }

    resultDetails.innerHTML = html;
    showProductCards(data);

    // Alternative button click - switch to alternative classification
    var altBtn = resultDetails.querySelector(".lf-cam__alt-btn");
    if (altBtn && data.alternative) {
      altBtn.addEventListener("click", function () {
        var altData = Object.assign({}, data);
        altData.subtype_id = data.alternative.subtype_id;
        altData.stone_type = (data.alternative.info && data.alternative.info.name) || data.alternative.subtype_id;
        altData.recommended_products = data.alternative.products || data.recommended_products;
        altData.confidence = Math.max(0.5, (data.confidence || 0) - 0.1);
        altData.reasoning = null;
        altData.alternative = {
          subtype_id: data.subtype_id,
          info: data.encyclopedia ? { name: data.stone_type, description: data.encyclopedia.description } : null,
          products: data.recommended_products,
        };
        submitFeedback(-1, data.alternative.subtype_id, altData.stone_type);
        showSurfaceResults(altData);
      });
    }

    renderFeedbackUI(data);
  }

  function showProblemResults(data) {
    resultTitle.textContent = data.problem_name || "Unknown Problem";
    showConfidence(data.confidence);

    var html = "";

    if (data.severity) {
      var sevClass = data.severity === "severe" ? "severe" : data.severity === "moderate" ? "moderate" : "light";
      html += '<div class="lf-cam__severity lf-cam__severity--' + sevClass + '">';
      html += "<strong>" + L("severity") + ":</strong> " + esc(data.severity);
      html += "</div>";
    }

    if (data.diagnosis && data.diagnosis.length) {
      html += '<div class="lf-cam__detail-section">';
      html += "<h5>" + L("diagnosis") + "</h5><ul>";
      data.diagnosis.forEach(function (d) { html += "<li>" + esc(d) + "</li>"; });
      html += "</ul></div>";
    }

    if (data.immediate_action) {
      html += '<div class="lf-cam__detail-section lf-cam__detail-section--action">';
      html += "<h5>" + L("immediateAction") + "</h5>";
      html += "<p>" + esc(data.immediate_action) + "</p></div>";
    }

    if (data.treatment_summary) {
      html += '<div class="lf-cam__detail-section">';
      html += "<h5>" + L("treatment") + "</h5>";
      html += "<p>" + esc(data.treatment_summary) + "</p></div>";
    }

    if (data.guide) {
      html += '<div class="lf-cam__detail-section">';
      html += "<h5>" + esc(data.guide.title || "") + "</h5>";
      if (data.guide.description) html += "<p>" + esc(data.guide.description) + "</p>";
      html += "</div>";
    }

    // Knowledge link to problem guide page
    if (data.problem_id) {
      html += '<div class="lf-cam__knowledge">';
      html += '<a class="lf-cam__klink" href="' + rootUrl + '/pages/problem-' + esc(data.problem_id) + '">';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg> ';
      html += L("treatmentGuide") + ": " + esc(data.problem_name) + "</a>";
      html += "</div>";
    }

    resultDetails.innerHTML = html;
    showProductCards(data);
    renderFeedbackUI(data);
  }

  function showConfidence(conf) {
    if (!conf || conf <= 0) { confidenceEl.innerHTML = ""; return; }
    var pct = Math.round(conf * 100);
    var cls = pct >= 80 ? "high" : pct >= 50 ? "mid" : "low";
    confidenceEl.innerHTML = '<span class="lf-cam__conf lf-cam__conf--' + cls + '">' + pct + "%</span>";
  }

  // Vervangt de oude, statische kaart (kale link, geraden afbeelding) door
  // een live /recommend-aanroep + de gedeelde renderer (DECK-195, fase 3).
  // "Nooit doodlopen": /recommend's eigen fallback-trap (T1-T4) garandeert
  // altijd koopbare producten; alleen bij een netwerkfout blijft het paneel
  // leeg (rest van het resultaat - method/tips/encyclopedie - blijft staan).
  function showProductCards(data) {
    var surfaceId = data.surface_id || "";
    var subtypeId = data.subtype_id || "";
    var problemId = data.problem_id || "";
    if (!surfaceId && !problemId) { productsEl.innerHTML = ""; return; }

    productsEl.innerHTML = '<p class="lf-cards__loading">' + esc(L("loadingProducts") || "") + "</p>";
    var params = "surface=" + encodeURIComponent(surfaceId || (problemId ? "natural-stone" : ""))
      + "&subtype=" + encodeURIComponent(subtypeId)
      + "&problem=" + encodeURIComponent(problemId)
      + "&country=" + encodeURIComponent(country)
      + "&locale=" + encodeURIComponent(lang);

    fetch(proxyUrl + "/recommend?" + params)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (result) {
        if (!result || !result.products || !result.products.length || !window.LithofinderCards) {
          productsEl.innerHTML = "";
          return;
        }
        var html = "<h5>" + L("recommended") + "</h5>";
        html += window.LithofinderCards.render(result.products, {
          source: "camera",
          scanId: lastRecognitionId,
          subtype: subtypeId,
          problem: problemId,
        });
        productsEl.innerHTML = html;
      })
      .catch(function () { productsEl.innerHTML = ""; });
  }

  // ── Tier 2: direct-antwoord (plan A2, fase 6) ─────────────────────
  // Het v5-model is zeker genoeg (subtype-confidence >= T_direct[klasse],
  // klasse staat in DIRECT_ELIGIBLE) om meteen een resultaatkaart te tonen,
  // met een een-taps "Klopt / Nee, iets anders"-bevestiging eronder. Bij
  // "Klopt" is de bevestiging zelf het sterkste labelsignaal (weight 1.0,
  // server-side toegekend aan label_source "direct-confirm"). Bij "Nee,
  // iets anders" valt het door naar tier 3 (top-3) of tier 4 (groep).
  async function showDirectAnswer(stage1, surfaceId) {
    var enrichResult = await callEnrich({ mode: "surface", surface_id: surfaceId, subtype_id: stage1.subtype_id });
    var data = Object.assign({
      stone_type: humanName(stage1.subtype_id),
      minc_category: stage1.minc_category,
      surface_id: surfaceId,
      subtype_id: stage1.subtype_id,
      confidence: stage1.subtype_confidence,
      is_lithofin_relevant: true,
      _stage: "browser-direct"
    }, enrichResult || {});
    lastPrediction = data;
    showResults(data);
    renderDirectConfirmBar(stage1, surfaceId);
  }

  function renderDirectConfirmBar(stage1, surfaceId) {
    var old = document.getElementById("lf-cam-direct-confirm");
    if (old) old.remove();

    var html = '<div class="lf-cam__direct-confirm" id="lf-cam-direct-confirm">';
    html += '<button type="button" class="lf-cam__chip lf-cam__chip--primary" id="lf-cam-direct-yes">' + esc(L("directConfirmYes") || "Klopt") + "</button>";
    html += '<button type="button" class="lf-cam__chip lf-cam__chip--ghost" id="lf-cam-direct-no">' + esc(L("directConfirmNo") || "Nee, iets anders") + "</button>";
    html += "</div>";
    resultDetails.insertAdjacentHTML("afterbegin", html);

    var yesBtn = document.getElementById("lf-cam-direct-yes");
    var noBtn = document.getElementById("lf-cam-direct-no");
    if (yesBtn) {
      yesBtn.addEventListener("click", function () {
        patchScanLabel(lastRecognitionId, stage1.subtype_id, "direct-confirm");
        var el = document.getElementById("lf-cam-direct-confirm");
        if (el) el.remove();
      });
    }
    if (noBtn) {
      noBtn.addEventListener("click", function () {
        var el = document.getElementById("lf-cam-direct-confirm");
        if (el) el.remove();
        if (stage1.top3_subtypes && stage1.top3_subtypes.length >= 2) {
          showTop3Assist(stage1, surfaceId, /* excludeTop */ true);
        } else {
          showGroupPicker(surfaceId, stage1);
        }
      });
    }
  }

  // ── Tier 3: top-3-assist (plan A2, fase 6) ────────────────────────
  // De oppervlaktegroep is zeker genoeg (surface-confidence >= 0.65) maar
  // geen enkel subtype haalt de direct-antwoord-drempel: toon de groep-
  // brede kaart + drie subtype-kandidaten uit dezelfde groep als chips.
  // De tap van de gebruiker op een kandidaat IS het label (top3-choice,
  // gewicht 0.9); "geen van deze" valt door naar de bestaande groep-kiezer.
  async function showTop3Assist(stage1, surfaceId, excludeTop) {
    var enrichResult = await callEnrich({ mode: "surface", surface_id: surfaceId });
    var data = Object.assign({
      stone_type: humanName(stage1.minc_category),
      minc_category: stage1.minc_category,
      surface_id: surfaceId,
      subtype_id: null,
      confidence: stage1.surface_confidence,
      is_lithofin_relevant: true,
      _stage: "browser-top3"
    }, enrichResult || {});
    lastPrediction = data;
    showResults(data);

    var candidates = stage1.top3_subtypes.slice(0, 3);
    if (excludeTop && candidates.length) candidates = candidates.slice(1);
    renderTop3Chips(candidates, surfaceId);
  }

  function renderTop3Chips(candidates, surfaceId) {
    var old = document.getElementById("lf-cam-top3-picker");
    if (old) old.remove();
    if (!candidates || !candidates.length) return;

    var html = '<div class="lf-cam__top3-picker" id="lf-cam-top3-picker">';
    html += "<p>" + esc(L("top3Prompt") || "Lijkt het op een van deze?") + "</p>";
    html += '<div class="lf-cam__top3-cards">';
    candidates.forEach(function (c) {
      // Referentiefoto's (fase 6-vervolg): alleen voor de Tier A-subtypes met
      // een CC-licentiefoto met geverifieerde herkomst (provenance.csv, fase 4);
      // ontbreekt die, dan valt de kaart terug op tekst-only (was al zo).
      var ref = (window.LithofinderReferencePhotos || {})[c.subtype_id];
      html += '<button type="button" class="lf-cam__top3-card" data-subtype="' + esc(c.subtype_id) + '">';
      if (ref) {
        html += '<img class="lf-cam__top3-photo" src="' + esc(ref.url) + '" alt="" loading="lazy" width="120" height="90">';
      }
      html += '<span class="lf-cam__top3-name">' + esc(humanName(c.subtype_id)) + "</span>";
      html += '<span class="lf-cam__top3-conf">' + Math.round(c.confidence * 100) + "%</span>";
      if (ref) {
        // CC-attributie-verplichting: auteur + licentie, gelinkt naar de licentietekst.
        html += '<a class="lf-cam__top3-credit" href="' + esc(ref.license_url || ref.page_url) + '" target="_blank" rel="noopener noreferrer nofollow" onclick="event.stopPropagation()">';
        html += "© " + esc(ref.author) + " · " + esc(ref.license);
        html += "</a>";
      }
      html += "</button>";
    });
    html += "</div>";
    html += '<button type="button" class="lf-cam__chip lf-cam__chip--ghost" id="lf-cam-top3-none">' + esc(L("noneOfThese") || "Geen van deze / andere groep") + "</button>";
    html += "</div>";
    productsEl.insertAdjacentHTML("beforebegin", html);

    document.querySelectorAll("#lf-cam-top3-picker [data-subtype]").forEach(function (btn) {
      btn.addEventListener("click", async function () {
        var subtypeId = btn.dataset.subtype;
        patchScanLabel(lastRecognitionId, subtypeId, "top3-choice");
        var enrichResult = await callEnrich({ mode: "surface", surface_id: surfaceId, subtype_id: subtypeId });
        var data = Object.assign({
          stone_type: humanName(subtypeId),
          surface_id: surfaceId,
          subtype_id: subtypeId,
          confidence: 1,
          is_lithofin_relevant: true,
          _stage: "browser-top3-choice"
        }, enrichResult || {});
        lastPrediction = data;
        showResults(data);
      });
    });
    var noneBtn = document.getElementById("lf-cam-top3-none");
    if (noneBtn) {
      noneBtn.addEventListener("click", function () {
        var el = document.getElementById("lf-cam-top3-picker");
        if (el) el.remove();
        showGroupPicker(surfaceId, null);
      });
    }
  }

  // ── Type-kiezer (plan A2: "alleen-groep" -> chips) ────────────────
  // Getoond onder een groep-brede resultaatkaart zodra het on-device model
  // wel de oppervlaktegroep gaf maar (nog) geen subtype - de tap van de
  // gebruiker IS het label (label_source: group-then-wizard).
  function showGroupPicker(surfaceId, stage1) {
    var old = document.getElementById("lf-cam-group-picker");
    if (old) old.remove();

    var ld = window.LithofinderData;
    var groups = (ld && ld.surfaceTypes) || [];
    var group = surfaceId ? groups.filter(function (g) { return g.id === surfaceId; })[0] : null;

    var html = '<div class="lf-cam__group-picker" id="lf-cam-group-picker">';
    html += "<p class=\"lf-cam__picker-q\">" + (L("whichTypeExactly") || "Welk type precies?") + "</p>";

    if (group && group.subtypes && group.subtypes.length) {
      html += '<div class="lf-cam__chip-row">';
      group.subtypes.forEach(function (st) {
        html += '<button type="button" class="lf-cam__chip" data-subtype="' + esc(st.id) + '">' + esc(subtypeName(st.id)) + "</button>";
      });
      html += "</div>";
    }

    html += '<button type="button" class="lf-cam__chip lf-cam__chip--ghost" id="lf-cam-group-picker-more">' + (L("noneOfThese") || "Geen van deze / andere groep") + "</button>";
    html += "</div>";

    resultDetails.insertAdjacentHTML("beforeend", html);

    var picker = document.getElementById("lf-cam-group-picker");
    picker.querySelectorAll("[data-subtype]").forEach(function (btn) {
      btn.addEventListener("click", function () { chooseSubtype(surfaceId, btn.dataset.subtype, stage1); });
    });
    var moreBtn = document.getElementById("lf-cam-group-picker-more");
    if (moreBtn) moreBtn.addEventListener("click", function () { renderGroupChips(surfaceId); });
  }

  // Volledige groepkeuze (alle 4 groepen, plan A2 regel 4 "alleen-groep" /
  // wizard-fallback) - gebruikt als het model geen groep gaf of de
  // gebruiker "geen van deze" koos.
  function renderGroupChips(preselectSurfaceId) {
    var old = document.getElementById("lf-cam-group-picker");
    if (old) old.remove();
    var ld = window.LithofinderData;
    var groups = (ld && ld.surfaceTypes) || [];
    if (!groups.length) return;

    var html = '<div class="lf-cam__group-picker" id="lf-cam-group-picker">';
    html += "<p class=\"lf-cam__picker-q\">" + (L("whichSurfaceGroup") || "Welk type oppervlak is het?") + "</p>";
    html += '<div class="lf-cam__chip-row">';
    groups.forEach(function (g) {
      html += '<button type="button" class="lf-cam__chip" data-group="' + esc(g.id) + '">' + esc(groupName(g.id)) + "</button>";
    });
    html += "</div></div>";
    resultDetails.insertAdjacentHTML("beforeend", html);

    document.querySelectorAll("#lf-cam-group-picker [data-group]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var gid = btn.dataset.group;
        patchScanLabel(lastRecognitionId, gid, "group-then-wizard");
        callEnrich({ mode: "surface", surface_id: gid }).then(function (enrichResult) {
          var data = Object.assign({ stone_type: groupName(gid), surface_id: gid, subtype_id: null, is_lithofin_relevant: true, _stage: "wizard-group" }, enrichResult || {});
          lastPrediction = data;
          showResults(data);
          showGroupPicker(gid, null);
        });
      });
    });
  }

  function chooseSubtype(surfaceId, subtypeId, stage1) {
    patchScanLabel(lastRecognitionId, subtypeId, "group-then-wizard");
    callEnrich({ mode: "surface", surface_id: surfaceId, subtype_id: subtypeId }).then(function (enrichResult) {
      var data = Object.assign({
        stone_type: (enrichResult && enrichResult.stone_type) || subtypeName(subtypeId),
        surface_id: surfaceId,
        subtype_id: subtypeId,
        confidence: stage1 ? stage1.confidence : null,
        is_lithofin_relevant: true,
        _stage: "group-then-wizard"
      }, enrichResult || {});
      lastPrediction = data;
      showResults(data);
      var picker = document.getElementById("lf-cam-group-picker");
      if (picker) picker.remove();
    });
  }

  function subtypeName(id) {
    var names = window.LithofinderSubtypeNames;
    if (names && names[id]) return names[id];
    return id.replace(/-/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function groupName(id) {
    var names = { "natural-stone": "Natural stone", "ceramics": "Ceramics / tiles", "artificial-stone": "Artificial stone", "cotto-terracotta": "Cotto / terracotta" };
    return (window.LithofinderGroupNames && window.LithofinderGroupNames[id]) || names[id] || id;
  }

  // ── Probleem-kiezer (plan A3: dag-1 = chips, geen camera-model) ───
  function renderProblemPicker(surfaceHint) {
    var ld = window.LithofinderData;
    var problems = (surfaceHint && ld && ld.surfaceProblems && ld.surfaceProblems[surfaceHint])
      || flattenAllProblems(ld);
    if (!problems || !problems.length) {
      resultDetails.innerHTML = "<p>" + (L("noProblemsAvailable") || "") + "</p>";
      return;
    }

    var html = '<div class="lf-cam__group-picker" id="lf-cam-problem-picker">';
    if (!surfaceHint) {
      html += "<p class=\"lf-cam__picker-q\">" + (L("selectSurfaceFirst") || "Weet je het oppervlak? Dat helpt.") + "</p>";
    }
    html += '<div class="lf-cam__chip-row">';
    problems.forEach(function (pid) {
      html += '<button type="button" class="lf-cam__chip" data-problem="' + esc(pid) + '">' + esc(problemName(pid)) + "</button>";
    });
    html += "</div></div>";
    resultDetails.innerHTML = html;

    document.querySelectorAll("#lf-cam-problem-picker [data-problem]").forEach(function (btn) {
      btn.addEventListener("click", function () { chooseProblem(surfaceHint, btn.dataset.problem); });
    });
  }

  function flattenAllProblems(ld) {
    if (!ld || !ld.surfaceProblems) return [];
    var seen = {};
    var all = [];
    Object.keys(ld.surfaceProblems).forEach(function (g) {
      ld.surfaceProblems[g].forEach(function (p) { if (!seen[p]) { seen[p] = true; all.push(p); } });
    });
    return all;
  }

  function chooseProblem(surfaceHint, problemId) {
    patchScanLabel(lastRecognitionId, problemId, "wizard-full");
    callEnrich({ mode: "problem", problem_id: problemId, surface_id: surfaceHint || null }).then(function (enrichResult) {
      var data = Object.assign({
        problem_name: problemName(problemId),
        problem_id: problemId,
        surface_id: surfaceHint || null,
        confidence: null
      }, enrichResult || {});
      lastPrediction = data;
      showResults(data);
    });
  }

  function problemName(id) {
    var names = window.LithofinderProblemNames;
    if (names && names[id]) return names[id];
    return id.replace(/-/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // ── Feedback UI ──────────────────────────────────────────────────
  function renderFeedbackUI(data) {
    // Remove previous feedback UI if exists
    var old = document.getElementById("lf-cam-feedback");
    if (old) old.remove();
    var oldCorr = document.getElementById("lf-cam-correction");
    if (oldCorr) oldCorr.remove();

    var prominent = data._feedback_prompt;

    var html = '<div class="lf-cam__feedback' + (prominent ? " lf-cam__feedback--prominent" : "") + '" id="lf-cam-feedback">';
    html += '<p class="lf-cam__feedback-q">' + (prominent ? L("helpUs") : L("wasThisCorrect")) + '</p>';
    html += '<div class="lf-cam__feedback-btns">';
    html += '<button class="lf-cam__vote lf-cam__vote--up" data-vote="1" aria-label="' + L("ariaCorrect") + '">';
    html += '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>';
    html += '</button>';
    html += '<button class="lf-cam__vote lf-cam__vote--down" data-vote="-1" aria-label="' + L("ariaWrong") + '">';
    html += '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg>';
    html += '</button>';
    html += '</div></div>';

    // Correction panel (hidden until downvote)
    html += '<div class="lf-cam__correction" id="lf-cam-correction" style="display:none">';
    html += '<p>' + L("whatIsIt") + '</p>';
    html += '<select id="lf-cam-correct-type"><option value="">- ' + L("selectType") + ' -</option>';

    // Populate subtypes from LithofinderData
    var ld = window.LithofinderData;
    if (ld && ld.surfaceTypes) {
      ld.surfaceTypes.forEach(function (group) {
        if (group.subtypes) {
          group.subtypes.forEach(function (st) {
            html += '<option value="' + esc(st.id) + '">' + esc(st.name || st.id) + '</option>';
          });
        }
      });
    }
    html += '</select>';
    html += '<input type="text" id="lf-cam-correct-name" placeholder="' + L("moreSpecific") + '" class="lf-cam__correct-input">';
    html += '<button class="lf-cam__submit-fb" id="lf-cam-submit-fb">' + L("sendFeedback") + '</button>';
    html += '</div>';

    resultDetails.insertAdjacentHTML("beforeend", html);

    // Wire up vote buttons
    var fbEl = document.getElementById("lf-cam-feedback");
    fbEl.querySelectorAll(".lf-cam__vote").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var vote = parseInt(btn.dataset.vote, 10);
        if (vote === 1) {
          submitFeedback(1, null, null);
          fbEl.innerHTML = '<p class="lf-cam__feedback-thanks">' + L("thanks") + '</p>';
          var corr = document.getElementById("lf-cam-correction");
          if (corr) corr.style.display = "none";
        } else {
          btn.classList.add("active");
          var corr = document.getElementById("lf-cam-correction");
          if (corr) corr.style.display = "block";
        }
      });
    });

    // Wire up submit correction
    var submitBtn = document.getElementById("lf-cam-submit-fb");
    if (submitBtn) {
      submitBtn.addEventListener("click", function () {
        var typeSelect = document.getElementById("lf-cam-correct-type");
        var nameInput = document.getElementById("lf-cam-correct-name");
        var correctedType = typeSelect ? typeSelect.value : "";
        var correctedName = nameInput ? nameInput.value.trim() : "";
        if (!correctedType && !correctedName) return;
        submitFeedback(-1, correctedType, correctedName);
        fbEl.innerHTML = '<p class="lf-cam__feedback-thanks">' + L("thanks") + '</p>';
        var corr = document.getElementById("lf-cam-correction");
        if (corr) corr.style.display = "none";
      });
    }
  }

  async function submitFeedback(vote, correctedSubtype, correctedName) {
    try {
      var fd = new FormData();
      fd.append("recognition_id", lastRecognitionId || "");
      fd.append("vote", String(vote));
      fd.append("mode", currentMode);
      if (lastPrediction) {
        fd.append("predicted_surface_id", lastPrediction.surface_id || "");
        fd.append("predicted_subtype_id", lastPrediction.subtype_id || "");
        fd.append("predicted_problem_id", lastPrediction.problem_id || "");
        fd.append("predicted_name", lastPrediction.stone_type || lastPrediction.problem_name || "");
        fd.append("confidence", String(lastPrediction.confidence || 0));
        fd.append("stage1_source", lastPrediction._stage1_source || "");
      }
      if (correctedSubtype) fd.append("corrected_subtype_id", correctedSubtype);
      if (correctedName) fd.append("corrected_name", correctedName);
      if (lastCapturedBlob) fd.append("image", lastCapturedBlob, "feedback.jpg");
      fd.append("locale", lang);

      fetch(proxyUrl + "/feedback", { method: "POST", body: fd });
    } catch (e) {
      // Feedback failure should never disrupt UX
    }
  }

  // ── Double-tap to re-scan ──────────────────────────────────────────
  var lastTapTime = 0;
  results.addEventListener("touchend", function (e) {
    var now = Date.now();
    if (now - lastTapTime < 300) {
      e.preventDefault();
      resetToViewfinder();
      startStream();
      setTimeout(function () { captureAndAnalyze(true); }, 300);
    }
    lastTapTime = now;
  });

  // Desktop: double-click
  results.addEventListener("dblclick", function (e) {
    if (e.target.closest(".lf-cam__vote, .lf-cam__correction, .lf-cam__product-card, a")) return;
    resetToViewfinder();
    startStream();
    setTimeout(function () { captureAndAnalyze(true); }, 300);
  });

  // ── Retake ────────────────────────────────────────────────────────
  retakeBtn.addEventListener("click", function () {
    resetToViewfinder();
    startStream();
  });

  function resetToViewfinder() {
    results.style.display = "none";
    analyzingEl.style.display = "none";
    errorEl.style.display = "none";
    guide.style.display = "flex";
    controls.style.display = "flex";
    video.style.display = "block";
    lastRecognitionId = null;
    lastPrediction = null;
    lastCapturedBlob = null;
    autoCaptureTriggered = false;
    frameHistory = [];
    streakCategory = null;
    streakCount = 0;
    resetCaptureProgress();
    hint.textContent = currentMode === "problem" ? L("hintProblem") : L("hintSurface");
    hint.style.background = "rgba(0, 0, 0, 0.4)";
    if (stream && currentMode === "surface") startAutoDetect();
  }

  // ── Helpers ───────────────────────────────────────────────────────
  function esc(str) {
    var el = document.createElement("span");
    el.textContent = str || "";
    return el.innerHTML;
  }

  function resizeImage(file, maxSize) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        var w = img.width, h = img.height;
        if (w <= maxSize && h <= maxSize) {
          resolve(file);
          return;
        }
        var ratio = Math.min(maxSize / w, maxSize / h);
        var nw = Math.round(w * ratio), nh = Math.round(h * ratio);
        var c = document.createElement("canvas");
        c.width = nw;
        c.height = nh;
        c.getContext("2d").drawImage(img, 0, 0, nw, nh);
        c.toBlob(function (blob) {
          if (blob) { resolve(blob); return; }
          // iOS Safari fallback
          var dataUrl = c.toDataURL("image/jpeg", 0.85);
          fetch(dataUrl).then(function (r) { return r.blob(); }).then(resolve);
        }, "image/jpeg", 0.85);
      };
      img.src = URL.createObjectURL(file);
    });
  }

  // ── Accept file from wizard dropzone via custom event ────────────
  root.addEventListener("lf-camera-file", function (e) {
    var file = e.detail && e.detail.file;
    if (!file) return;
    (async function () {
      var blob = await resizeImage(file, 1024);
      await analyzeImage(blob);
    })();
  });

  // ── Emit recognition results for wizard integration ──────────────
  function emitResult(data) {
    // Store last scan for chat context bridge (expires after 10 min)
    if (data) {
      window.__lithofinLastScan = {
        mode: currentMode,
        surface_id: data.surface_id || null,
        subtype_id: data.subtype_id || null,
        problem_id: data.problem_id || null,
        stone_type: data.stone_type || null,
        problem_name: data.problem_name || null,
        confidence: data.confidence || 0,
        timestamp: Date.now()
      };
    }
    // Emit for wizard integration (surface results)
    if (data && (data.surface_id || data.minc_category)) {
      window.dispatchEvent(new CustomEvent("lf-camera-result", { detail: data }));
    }
  }

  // ── Knowledge page scan buttons (open camera from stone/problem pages) ──
  document.querySelectorAll(".knowledge-scan-btn").forEach(function (btn) {
    btn.addEventListener("click", function () {
      var mode = btn.dataset.scanMode || "surface";
      var modeBtn = root.querySelector('.lf-cam__mode[data-mode="' + mode + '"]');
      if (modeBtn) modeBtn.click();
      openCamera();
    });
  });

})();

